// Configuração centralizada da API e mídia.
// Toda imagem dinâmica (imóvel, usuário, banner, parceiro, empreendimento) vem do
// container livestay-backend — nunca do bundle/nginx do frontend.

const trimTrailingSlash = (url: string) => url.replace(/\/$/, '');

/**
 * Base URL do serviço API (Docker: livestay-backend na porta 3001).
 * - VITE_API_URL no build → usa esse valor.
 * - Browser sem env e frontend em :3000 → http://{host}:3001 (Docker local).
 * - Caso contrário → mesma origem (proxy reverso em produção).
 */
/** Sempre aponta para o serviço livestay-backend — nunca para o nginx do site. */
const getApiUrl = (): string => {
  const raw = import.meta.env.VITE_API_URL;
  if (typeof raw === 'string' && raw.trim() !== '') {
    return trimTrailingSlash(raw.trim());
  }
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    return `${protocol}//${hostname}:3001`;
  }
  return 'http://localhost:3001';
};

export const API_BASE_URL = getApiUrl();

export const getApiEndpoint = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE_URL) return cleanPath;
  return `${API_BASE_URL}${cleanPath}`;
};

/** Arquivo servido pelo Nest em /api/media/... */
export const getApiMediaUrl = (...segments: string[]): string => {
  const path = segments
    .map((s) => s.replace(/^\/+/, '').replace(/\/+$/, ''))
    .filter(Boolean)
    .join('/');
  return getApiEndpoint(`api/media/${path}`);
};

function basenameOnly(value: string): string {
  const noQuery = value.split('?')[0].split('#')[0];
  const parts = noQuery.replace(/\\/g, '/').split('/').filter(Boolean);
  return parts[parts.length - 1] ?? noQuery;
}

/**
 * @deprecated Use helpers específicos (getPropertyImageUrl, etc.). Mantido para paths legados.
 */
export const getPublicUrl = (mediaPath: string): string => {
  const clean = mediaPath.replace(/^\/+/, '');
  if (!API_BASE_URL) return `/${clean}`;
  return `${API_BASE_URL}/${clean}`;
};

export const getPropertyImageUrl = (
  propertyId: number,
  filename: string,
  timestamp?: number,
): string => {
  const ts = timestamp ? `?t=${timestamp}` : '';
  const normalized = filename.replace(/^\/+/, '');
  if (normalized.startsWith('thumb/')) {
    const thumbFile = basenameOnly(normalized.slice('thumb/'.length));
    return getApiMediaUrl(
      'properties',
      String(propertyId),
      'thumb',
      encodeURIComponent(thumbFile),
    ).concat(ts);
  }
  const base = basenameOnly(normalized);
  return getApiMediaUrl(
    'properties',
    String(propertyId),
    encodeURIComponent(base),
  ).concat(ts);
};

export const getUserImageUrl = (userId: number, filename: string): string => {
  return getApiMediaUrl(
    'users',
    String(userId),
    encodeURIComponent(basenameOnly(filename)),
  );
};

export const getPartnerLogoUrl = (logoPath: string | null | undefined): string => {
  if (!logoPath || (typeof logoPath === 'string' && logoPath.trim() === '')) {
    return '/placeholder.svg';
  }
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  return getApiMediaUrl('partners', encodeURIComponent(basenameOnly(logoPath)));
};

export const getDevelopmentLogoUrl = (
  logoPath: string | null | undefined,
): string => {
  if (!logoPath || (typeof logoPath === 'string' && logoPath.trim() === '')) {
    return '/placeholder.svg';
  }
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  return getApiMediaUrl(
    'developments',
    encodeURIComponent(basenameOnly(logoPath)),
  );
};

/** Qualquer path de upload do backend → URL na API (:3001 /api/media/...). */
export const mediaUrl = (path: string | null | undefined): string => {
  if (!path || !path.trim()) return '/placeholder.svg';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return getDevelopmentLogoUrl(path);
};

export const getBannerImageUrl = (imagePath: string | null | undefined): string => {
  if (!imagePath || (typeof imagePath === 'string' && imagePath.trim() === '')) {
    return '/placeholder.svg';
  }
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  return getApiMediaUrl('banners', encodeURIComponent(basenameOnly(imagePath)));
};

export default {
  API_BASE_URL,
  getApiEndpoint,
  getApiMediaUrl,
  mediaUrl,
  getPublicUrl,
  getPropertyImageUrl,
  getUserImageUrl,
  getPartnerLogoUrl,
  getDevelopmentLogoUrl,
  getBannerImageUrl,
};
